import { useCallback } from 'react';
import { cloneDeep } from 'lodash-es';
import { useDispatch, useSelector } from 'react-redux';

import { useGetValidConfirmationForService } from './use-get-valid-confirmation-for-service';
import { useDdpCountryConfig } from '../../hooks/use-ddp-country-config';
import { useDdpEligibleCarriers } from '../../hooks/use-ddp-eligible-carriers';
import { useBillDutiesAndTaxesToPayor } from '../../hooks/use-seller-setting';
import { cleanOptionsBasedOnSelectedServiceId } from '../../services/other-shipping-options/constants';
import * as Actions from '../../store/csw/csw.actions';
import { getConfirmation } from '../../store/csw/csw.helpers';
import Global from '../../store/global-selectors';

/**
 * Apply DDP (Delivered Duty Paid) logic when carrier/service changes.
 * DDP is available for eligible countries (US, CA, etc.) when the feature flag is enabled,
 * the seller has enabled the DDP setting, the carrier supports DDP for that country,
 * and the shipment is international.
 * @param {string} serviceId - The service ID to check
 * @param {object} currentOptions - The current shipping options
 * @param {object[]} sellerServices - Array of seller services
 * @param {boolean} DDPSellerSettingEnabled - Whether the seller has enabled DDP
 * @param {(id: string) => boolean} isDdpEligibleBySellerLabelProviderId - Function to check if carrier supports DDP
 * @param {boolean} isDdpAvailable - Whether DDP is available for seller's country
 * @param {boolean} isInternational - Whether the shipment is international
 * @returns {object} Updated options with DDP flag if applicable
 */
const applyDDPLogicOnServiceChange = (
  serviceId,
  currentOptions,
  sellerServices,
  DDPSellerSettingEnabled,
  isDdpEligibleBySellerLabelProviderId,
  isDdpAvailable,
  isInternational,
) => {
  const service = sellerServices.find((s) => serviceId === s.serviceId);
  const sellerLabelProviderId = service?.sellerLabelProviderId;

  // Check if carrier is DDP-eligible for seller's country
  if (!sellerLabelProviderId || !isDdpEligibleBySellerLabelProviderId(sellerLabelProviderId)) {
    return currentOptions;
  }

  // DDP must be available for seller's country (feature flag enabled + eligible country)
  if (!isDdpAvailable) {
    return currentOptions;
  }

  // DDP only applies to international shipments
  if (!isInternational) {
    return currentOptions;
  }

  if (DDPSellerSettingEnabled) {
    return {
      ...currentOptions,
      deliveredDutyPaid: true,
    };
  }

  return currentOptions;
};

export const useOtherShippingOptionsState = (fulfillmentPlanId, otherShippingOptions, onConfigurationChange) => {
  const dispatch = useDispatch();

  const sellerServices = useSelector(Global.selectSellerServices);
  const DDPSellerSettingsEnabled = useBillDutiesAndTaxesToPayor();

  // Get country-specific DDP availability
  const { isDdpAvailable } = useDdpCountryConfig();
  const { isDdpEligibleBySellerLabelProviderId } = useDdpEligibleCarriers();

  const isInternational = useSelector((state) => Global.selectEntitiesFulfillmentPlanIsInternational(state, { fulfillmentPlanId }));

  const onOtherShippingOptionsChange = useCallback(
    (_, newOptions) => {
      const payloadOptions = cloneDeep(newOptions.options);
      const copiedCurrentOptions = cloneDeep(otherShippingOptions);
      Object.keys(copiedCurrentOptions).forEach((option) => {
        if (payloadOptions[option] === null || payloadOptions[option] === false) {
          delete copiedCurrentOptions[option];
          delete payloadOptions[option];
        }
      });

      const defaultConfirmation = 'None';
      const confirmation = getConfirmation(payloadOptions) || copiedCurrentOptions.confirmation || defaultConfirmation;

      const update = {
        ...copiedCurrentOptions,
        ...payloadOptions,
        confirmation,
      };
      dispatch(Actions.updateShipmentConfiguration(fulfillmentPlanId, newOptions));
      onConfigurationChange({ options: update });
    },
    [dispatch, fulfillmentPlanId, otherShippingOptions, onConfigurationChange],
  );

  const getValidConfirmationForService = useGetValidConfirmationForService();
  const onServiceChangeUpdateOtherShippingOptions = useCallback(
    (serviceId, confirmationId) => {
      const newOptions = cleanOptionsBasedOnSelectedServiceId(serviceId, otherShippingOptions);
      const validatedConfirmationId = getValidConfirmationForService(serviceId, confirmationId);
      const optionsWithDDP = applyDDPLogicOnServiceChange(
        serviceId,
        newOptions,
        sellerServices,
        DDPSellerSettingsEnabled,
        isDdpEligibleBySellerLabelProviderId,
        isDdpAvailable,
        isInternational,
      );

      const mergedOptions = {
        ...optionsWithDDP,
        confirmation: validatedConfirmationId,
        confirmationEnumId: validatedConfirmationId,
      };

      onConfigurationChange({ options: mergedOptions });
      dispatch(Actions.updateShipmentConfigurationOptions(fulfillmentPlanId, 'confirmation', validatedConfirmationId));
      dispatch(Actions.updateShipmentConfigurationOptions(fulfillmentPlanId, 'confirmationEnumId', validatedConfirmationId));

      if (optionsWithDDP.deliveredDutyPaid !== newOptions.deliveredDutyPaid) {
        dispatch(Actions.updateShipmentConfigurationOptions(fulfillmentPlanId, 'deliveredDutyPaid', optionsWithDDP.deliveredDutyPaid));
      }
    },
    [
      otherShippingOptions,
      getValidConfirmationForService,
      sellerServices,
      DDPSellerSettingsEnabled,
      isDdpEligibleBySellerLabelProviderId,
      isDdpAvailable,
      isInternational,
      onConfigurationChange,
      dispatch,
      fulfillmentPlanId,
    ],
  );

  const onConfirmationChange = useCallback(
    (confirmationOptionId) => {
      const newOptions = {
        ...otherShippingOptions,
        // this seems weird but is true to the previous logic so nothing downstream should change
        confirmation: confirmationOptionId,
        confirmationEnumId: confirmationOptionId,
      };
      dispatch(Actions.updateShipmentConfigurationOptions(fulfillmentPlanId, 'confirmation', confirmationOptionId));
      dispatch(Actions.updateShipmentConfigurationOptions(fulfillmentPlanId, 'confirmationEnumId', confirmationOptionId));
      onConfigurationChange({ options: newOptions });
      return newOptions;
    },
    [dispatch, fulfillmentPlanId, otherShippingOptions, onConfigurationChange],
  );

  const onSelectedDutyOptionChange = useCallback(
    (value, availableDutiesOptions, rateRequestOptions) => {
      const newAdditionalOption = availableDutiesOptions.find(({ additionalOptionValue }) => additionalOptionValue.prepaidDuty === value);
      const cloned = cloneDeep(otherShippingOptions);
      const newOptions = {
        ...cloned,
        additionalOptions: [newAdditionalOption],
      };
      dispatch(Actions.updateShipmentConfigurationOptions(fulfillmentPlanId, 'additionalOptions', [newAdditionalOption]));
      onConfigurationChange({ options: newOptions }, rateRequestOptions);
    },
    [dispatch, fulfillmentPlanId, otherShippingOptions, onConfigurationChange],
  );

  return {
    onConfirmationChange,
    onOtherShippingOptionsChange,
    onSelectedDutyOptionChange,
    onServiceChangeUpdateOtherShippingOptions,
  };
};
